import type React from 'react';
import { z } from 'zod';

export const TYPE_LABELS: Record<string, string> = {
  INCOME: 'Entrada',
  EXPENSE: 'Saída',
  CONTRIBUTION: 'Contribuição',
};

export const TYPE_VARIANTS: Record<string, string> = {
  INCOME: 'success',
  EXPENSE: 'danger',
  CONTRIBUTION: 'info',
};

export const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PAID: 'Pago',
  CANCELLED: 'Cancelado',
  OVERDUE: 'Vencido',
  REFUNDED: 'Reembolsado',
  ARCHIVED: 'Arquivado',
};

export const STATUS_VARIANTS: Record<string, string> = {
  PENDING: 'warning',
  PAID: 'success',
  CANCELLED: 'default',
  OVERDUE: 'danger',
  REFUNDED: 'info',
  ARCHIVED: 'default',
};

export const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Dinheiro',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT_CARD: 'Cartão de débito',
  PIX: 'Pix',
  TRANSFER: 'Transferência',
  BANK_SLIP: 'Boleto',
  OTHER: 'Outro',
};

export function fmtCurrency(val: unknown): string {
  const n = Number(val);
  if (Number.isNaN(n)) return 'R$ 0,00';
  return `R$ ${n.toFixed(2)}`.replace('.', ',');
}

export const editFormSchema = z.object({
  description: z.string().min(1),
  amount: z.string().min(1),
  dueDate: z.string().optional(),
  paidAt: z.string().optional(),
  paymentMethod: z.string().optional(),
  receiptRef: z.string().optional(),
  categoryId: z.string().optional(),
  personId: z.string().optional(),
});

export type EditForm = z.infer<typeof editFormSchema>;

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

export function SkeletonDetail() {
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <div
        style={{
          width: 140,
          height: 14,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '1.5rem',
        }}
      />
      <div
        style={{
          width: '60%',
          height: 32,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '2rem',
        }}
      />
      {[1, 2].map((i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid var(--line)',
            padding: '1.25rem 1.5rem',
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              width: '30%',
              height: 14,
              background: 'var(--line-soft)',
              borderRadius: 8,
              marginBottom: '0.75rem',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ width: '60%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
            <div style={{ width: '40%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export const dlGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  gap: '0.5rem 1rem',
  fontSize: '0.875rem',
};

export const Dt = ({ children }: { children: React.ReactNode }) => (
  <dt style={{ color: 'var(--ink)', fontWeight: 600, margin: 0 }}>{children}</dt>
);

export const Dd = (props: React.HTMLAttributes<HTMLElement> & { children: React.ReactNode }) => (
  <dd style={{ margin: 0, color: 'var(--ink)', ...props.style }}>{props.children}</dd>
);

export const inputCss: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  marginBottom: '0.5rem',
  display: 'block',
  padding: 0,
};

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};
